import { useId, useMemo, useState, type ReactNode } from 'react';
import {
  activeTags,
  buildTree,
  canMoveUnder,
  canNestUnder,
  flattenTree,
  MAX_CATEGORY_DEPTH,
  tagUsage,
  taxonomyUsage,
  type Manifest,
  type AnyEvent,
  type TaxonomyNode,
} from '@intentionalist/shared';
import {
  createTaxonomyNode,
  deleteTag,
  deleteTaxonomyNode,
  mergeTagInto,
  renameTag,
  renameTaxonomyNode,
  reparentTaxonomyNode,
} from './mutations.js';

/**
 * Managing the taxonomy itself (Epic 4).
 *
 * Categories, locations and tags could be created from the capture form from the
 * day it existed, because that is when they are needed. Changing one afterwards
 * had no home at all: the events and the merge supported renaming, moving and
 * deleting, and nothing called them. This is that home.
 *
 * Every destructive action says what it will do to the items first. A category
 * is not a container -- deleting one leaves its items filed against an id that
 * no longer resolves -- and that is worth knowing before rather than after.
 */
type Kind = 'category' | 'location' | 'tag';

const PLURAL: Record<Kind, string> = {
  category: 'Categories',
  location: 'Locations',
  tag: 'Tags',
};

export function TaxonomyManager({
  manifest,
  version,
  onWrite,
}: {
  manifest: Manifest;
  version: number;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const [kind, setKind] = useState<Kind>('category');

  return (
    <div className="review organize">
      <h2>Organize</h2>

      <div className="actions">
        {(['category', 'location', 'tag'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
          >
            {PLURAL[option]}
          </button>
        ))}
      </div>

      {kind === 'tag' ? (
        <TagManager manifest={manifest} version={version} onWrite={onWrite} />
      ) : (
        <TreeManager
          key={kind}
          kind={kind}
          manifest={manifest}
          version={version}
          onWrite={onWrite}
        />
      )}
    </div>
  );
}

function TreeManager({
  kind,
  manifest,
  version,
  onWrite,
}: {
  kind: 'category' | 'location';
  manifest: Manifest;
  version: number;
  onWrite: (events: AnyEvent[]) => void;
}) {
  // FR-4.3 caps categories at three levels; FR-4.6 lets locations nest freely,
  // which is a real difference in the domain rather than an oversight.
  const maxDepth = kind === 'category' ? MAX_CATEGORY_DEPTH : Infinity;
  const records = kind === 'category' ? manifest.categories : manifest.locations;

  const nodes = useMemo(() => buildTree(records), [records, version]);
  const usage = useMemo(
    () => taxonomyUsage(manifest, kind === 'category' ? 'categoryId' : 'locationId'),
    [manifest, version, kind],
  );
  const [acting, setActing] = useState<{ id: string; what: 'rename' | 'move' | 'delete' } | null>(
    null,
  );

  const act = (id: string, what: 'rename' | 'move' | 'delete') =>
    setActing(acting?.id === id && acting.what === what ? null : { id, what });

  /**
   * The tree, rendered as a tree.
   *
   * It used to be flattened into one list, with each name given a left padding
   * proportional to its depth. That put the whole hierarchy on a single visual
   * cue -- and only half of one, since the line beneath the name stayed at the
   * margin and pulled against the indent -- while the document itself said "a
   * list of eleven peers", which is what a screen reader read out.
   *
   * Nesting the lists puts the structure in the markup instead. The indentation,
   * the rails the stylesheet draws, and what assistive technology announces then
   * all come from the same place, and none of them can drift from the others.
   */
  const rows = (list: readonly TaxonomyNode[]): ReactNode =>
    list.map((node) => {
      const count = usage.get(node.id) ?? 0;
      return (
        <li key={node.id}>
          <div className="catalog-row taxonomy-row">
            <div>
              <strong>{node.name}</strong>
              <div className="muted">
                {count === 0 ? 'nothing filed here' : `${count} item${count === 1 ? '' : 's'}`}
                {node.children.length > 0 && ` · ${node.children.length} inside`}
              </div>
            </div>

            <div className="actions row-actions">
              <button
                type="button"
                aria-label={`Rename ${node.name}`}
                onClick={() => act(node.id, 'rename')}
              >
                {acting?.id === node.id && acting.what === 'rename' ? 'Cancel' : 'Rename'}
              </button>
              <button
                type="button"
                aria-label={`Move ${node.name}`}
                onClick={() => act(node.id, 'move')}
              >
                {acting?.id === node.id && acting.what === 'move' ? 'Cancel' : 'Move'}
              </button>
              <button
                type="button"
                // Red only while it is the destructive press; as "Cancel" it is
                // the way back out, which is not destructive at all.
                className={acting?.id === node.id && acting.what === 'delete' ? '' : 'danger'}
                aria-label={`Delete ${node.name}`}
                onClick={() => act(node.id, 'delete')}
              >
                {acting?.id === node.id && acting.what === 'delete' ? 'Cancel' : 'Delete'}
              </button>
            </div>
          </div>

          {acting?.id === node.id && acting.what === 'rename' && (
            <Rename
              node={node}
              onDone={(name) => {
                onWrite(renameTaxonomyNode(kind, node.id, name));
                setActing(null);
              }}
            />
          )}

          {acting?.id === node.id && acting.what === 'move' && (
            <Move
              node={node}
              nodes={nodes}
              maxDepth={maxDepth}
              onDone={(parentId) => {
                onWrite(reparentTaxonomyNode(kind, node.id, parentId));
                setActing(null);
              }}
            />
          )}

          {acting?.id === node.id && acting.what === 'delete' && (
            <ConfirmDelete
              node={node}
              kind={kind}
              count={count}
              onConfirm={() => {
                onWrite(deleteTaxonomyNode(kind, node.id));
                setActing(null);
              }}
            />
          )}

          {node.children.length > 0 && (
            // Named, so moving through the lists by keyboard says whose children
            // these are rather than announcing a bare nested list.
            <ul className="taxonomy-children" aria-label={`Inside ${node.name}`}>
              {rows(node.children)}
            </ul>
          )}
        </li>
      );
    });

  return (
    <>
      <AddNode kind={kind} nodes={nodes} maxDepth={maxDepth} onWrite={onWrite} />

      {nodes.length === 0 ? (
        <p className="muted">
          None yet. They can also be created while filing an item, which is usually when one is
          wanted.
        </p>
      ) : (
        <ul className="catalog" aria-label={PLURAL[kind]}>
          {rows(nodes)}
        </ul>
      )}
    </>
  );
}

function AddNode({
  kind,
  nodes,
  maxDepth,
  onWrite,
}: {
  kind: 'category' | 'location';
  nodes: readonly TaxonomyNode[];
  maxDepth: number;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const ids = { name: useId(), parent: useId() };
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');

  const submit = () => {
    if (name.trim() === '') return;
    onWrite(createTaxonomyNode(kind, name, parentId === '' ? null : parentId).events);
    setName('');
    setParentId('');
  };

  return (
    <div className="grid-fields">
      <div className="field">
        <label htmlFor={ids.name}>New {kind}</label>
        <input
          id={ids.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="field">
        <label htmlFor={ids.parent}>Inside</label>
        <select id={ids.parent} value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">Nothing — a top level {kind}</option>
          {/* Offering a parent with no room would produce an error after the
              fact; leaving it out says so up front (FR-4.3). */}
          {flattenTree(nodes)
            .filter((node) => canNestUnder(nodes, node.id, maxDepth))
            .map((node) => (
              <option key={node.id} value={node.id}>
                {'  '.repeat(node.depth - 1)}
                {node.name}
              </option>
            ))}
        </select>
      </div>

      <div className="field">
        <button type="button" className="primary" onClick={submit} disabled={name.trim() === ''}>
          Add
        </button>
      </div>
    </div>
  );
}

/**
 * Renaming, which never changes identity.
 *
 * The id is what keeps "what did I own last March" answerable after a rename
 * (FR-9.10), so a rename is an edit to one field and nothing else.
 */
function Rename({ node, onDone }: { node: TaxonomyNode; onDone: (name: string) => void }) {
  const id = useId();
  const [name, setName] = useState(node.name);

  return (
    <div className="field">
      <label htmlFor={id}>New name</label>
      <input id={id} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      <p className="muted hint">
        Items keep their history: the name changes, what they are filed under does not.
      </p>
      <button
        type="button"
        className="primary"
        onClick={() => onDone(name)}
        disabled={name.trim() === ''}
      >
        Save
      </button>
    </div>
  );
}

/**
 * Moving a node, with only the destinations that can actually hold it.
 *
 * The filter is `canMoveUnder` rather than `canNestUnder`, which is the whole
 * reason this screen needed a new primitive: a move carries the node's children
 * with it, so the limit applies to its deepest descendant and not to the node
 * being moved.
 */
function Move({
  node,
  nodes,
  maxDepth,
  onDone,
}: {
  node: TaxonomyNode;
  nodes: readonly TaxonomyNode[];
  maxDepth: number;
  onDone: (parentId: string | null) => void;
}) {
  const id = useId();
  const [parentId, setParentId] = useState(node.parentId ?? '');
  const options = flattenTree(nodes).filter((candidate) =>
    canMoveUnder(nodes, node.id, candidate.id, maxDepth),
  );
  const toRoot = canMoveUnder(nodes, node.id, null, maxDepth);

  return (
    <div className="field">
      <label htmlFor={id}>Move inside</label>
      <select id={id} value={parentId} onChange={(e) => setParentId(e.target.value)}>
        {toRoot && <option value="">Nothing — make it top level</option>}
        {options.map((candidate) => (
          <option key={candidate.id} value={candidate.id}>
            {'  '.repeat(candidate.depth - 1)}
            {candidate.name}
          </option>
        ))}
      </select>
      {options.length === 0 && !toRoot && (
        <p className="muted hint">There is nowhere this can go without nesting too deeply.</p>
      )}
      {node.children.length > 0 && (
        <p className="muted hint">
          Everything inside it moves too — {node.children.length} immediately below.
        </p>
      )}
      <button
        type="button"
        className="primary"
        onClick={() => onDone(parentId === '' ? null : parentId)}
      >
        Move
      </button>
    </div>
  );
}

/**
 * Deleting, having said what it costs.
 *
 * Two facts, because both surprise people. Children are promoted rather than
 * deleted, so nothing filed beneath is lost. The node's own items are not
 * reassigned, so they will read as uncategorized -- a category is a label, not a
 * container, and there is nowhere for them to fall back to.
 */
function ConfirmDelete({
  node,
  kind,
  count,
  onConfirm,
}: {
  node: TaxonomyNode;
  kind: 'category' | 'location';
  count: number;
  onConfirm: () => void;
}) {
  return (
    <div className="warn" role="alert">
      <p>
        Delete <strong>{node.name}</strong>?
      </p>
      <ul>
        {count > 0 && (
          <li>
            {count} item{count === 1 ? '' : 's'} filed here will show as{' '}
            {kind === 'category' ? 'uncategorized' : 'having no location'}. Nothing else about them
            changes.
          </li>
        )}
        {node.children.length > 0 && (
          <li>
            {node.children.length} {node.children.length === 1 ? 'one' : 'ones'} inside it will move
            up a level rather than be deleted.
          </li>
        )}
        {count === 0 && node.children.length === 0 && <li>Nothing is using it.</li>}
      </ul>
      <button type="button" className="danger primary" onClick={onConfirm}>
        Delete it
      </button>
    </div>
  );
}

/**
 * Tags, where the interesting operation is the merge (FR-4.18).
 *
 * Creating a tag is not offered: tags are made by typing one onto an item, and
 * a tag that has never been applied to anything is not useful enough to be worth
 * a second way to make one.
 */
function TagManager({
  manifest,
  version,
  onWrite,
}: {
  manifest: Manifest;
  version: number;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const tags = useMemo(() => activeTags(manifest), [manifest, version]);
  const usage = useMemo(() => tagUsage(manifest), [manifest, version]);
  const [acting, setActing] = useState<{
    id: string;
    what: 'rename' | 'merge' | 'delete';
  } | null>(null);

  const act = (id: string, what: 'rename' | 'merge' | 'delete') =>
    setActing(acting?.id === id && acting.what === what ? null : { id, what });

  if (tags.length === 0) {
    return <p className="muted">No tags yet. They are made by typing one onto an item.</p>;
  }

  return (
    <ul className="catalog" aria-label="Tags">
      {tags.map((tag) => {
        const name = tag.displayName?.v ?? '';
        const count = usage.get(tag.tagId) ?? 0;
        return (
          <li key={tag.tagId}>
            <div className="catalog-row taxonomy-row">
              <div>
                <strong>{name}</strong>
                <div className="muted">
                  {count === 0 ? 'on nothing' : `on ${count} item${count === 1 ? '' : 's'}`}
                </div>
              </div>

              <div className="actions row-actions">
                <button
                  type="button"
                  aria-label={`Rename ${name}`}
                  onClick={() => act(tag.tagId, 'rename')}
                >
                  {acting?.id === tag.tagId && acting.what === 'rename' ? 'Cancel' : 'Rename'}
                </button>
                {tags.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Merge ${name}`}
                    onClick={() => act(tag.tagId, 'merge')}
                  >
                    {acting?.id === tag.tagId && acting.what === 'merge' ? 'Cancel' : 'Merge'}
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Delete ${name}`}
                  onClick={() => act(tag.tagId, 'delete')}
                >
                  {acting?.id === tag.tagId && acting.what === 'delete' ? 'Cancel' : 'Delete'}
                </button>
              </div>
            </div>

            {acting?.id === tag.tagId && acting.what === 'rename' && (
              <RenameTag
                name={name}
                onDone={(next) => {
                  onWrite(renameTag(tag.tagId, next));
                  setActing(null);
                }}
              />
            )}

            {acting?.id === tag.tagId && acting.what === 'merge' && (
              <MergeTag
                name={name}
                others={tags.filter((other) => other.tagId !== tag.tagId)}
                onDone={(intoTagId) => {
                  onWrite(mergeTagInto(tag.tagId, intoTagId));
                  setActing(null);
                }}
              />
            )}

            {acting?.id === tag.tagId && acting.what === 'delete' && (
              <div className="warn" role="alert">
                {/* FR-4.17 asks for the count before confirming, and the count is
                    the whole reason to pause: deleting a tag that is on sixty
                    items is a different act from deleting one on none. */}
                <p>
                  Delete <strong>{name}</strong>?
                  {count === 0
                    ? ' It is on nothing.'
                    : ` It will be removed from ${count} item${count === 1 ? '' : 's'}, which are not deleted.`}
                </p>
                {count > 0 && (
                  <p className="muted hint">
                    To keep the items grouped, merge it into another tag instead.
                  </p>
                )}
                <button
                  type="button"
                  className="danger primary"
                  onClick={() => {
                    onWrite(deleteTag(tag.tagId));
                    setActing(null);
                  }}
                >
                  Delete it
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function RenameTag({ name, onDone }: { name: string; onDone: (name: string) => void }) {
  const id = useId();
  const [next, setNext] = useState(name);

  return (
    <div className="field">
      <label htmlFor={id}>New name</label>
      <input id={id} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="off" />
      <button type="button" onClick={() => onDone(next)} disabled={next.trim() === ''}>
        Save
      </button>
    </div>
  );
}

/** FR-4.18. The applications survive, which is the point of a merge over a delete. */
function MergeTag({
  name,
  others,
  onDone,
}: {
  name: string;
  others: ReturnType<typeof activeTags>;
  onDone: (intoTagId: string) => void;
}) {
  const id = useId();
  const [into, setInto] = useState(others[0]?.tagId ?? '');

  return (
    <div className="field">
      <label htmlFor={id}>Fold {name} into</label>
      <select id={id} value={into} onChange={(e) => setInto(e.target.value)}>
        {others.map((other) => (
          <option key={other.tagId} value={other.tagId}>
            {other.displayName?.v ?? ''}
          </option>
        ))}
      </select>
      <p className="muted hint">
        Everything tagged {name} keeps the tag — it just reads as the other one from now on.
      </p>
      <button type="button" onClick={() => onDone(into)} disabled={into === ''}>
        Merge
      </button>
    </div>
  );
}
